import { Fragment, useEffect, useRef, useState } from 'react';
import type { Me } from '../auth/me';
import { SheetAddRow, SheetSection } from '../ui/Sheet';
import { LineComments, usePacklistComments } from './LineComments';
import { PacklistLineRow, type SheetColumns } from './PacklistLineRow';
import type { AddGear } from '../quotes/AddPricedLine';
import { QuickAdd } from './QuickAdd';
import { duplicateGroups, type LineView, type RuleOutcome, type SupplierSection } from './types';
import type { CatalogTarget } from '../nav/catalogLink';
import { SupplierLineRow, supplierGroupKey, type PlacedSupplierLine } from './versions';

const NO_CATEGORY = 'Bez kategorii';

type Props = {
  eventId: string;
  lines: LineView[];
  /** Catalog categories in the catalog order, with their full labels ("Nagłośnienie / Mikrofony"). */
  categories: { id: string; label: string }[];
  sourceNames: Map<string, string>;
  ruleOutcomes: readonly RuleOutcome[];
  editable: boolean;
  cols: SheetColumns;
  selected: ReadonlySet<string>;
  onToggleSelect: (lineId: string) => void;
  onRemove: (line: LineView) => void;
  onReload: () => void;
  /** The signed-in user: with it the lines get their comments (P11). */
  me?: Me | null;
  /** F28: "Dopożycz" at a line's shortage. */
  onSubrent?: ((line: LineView) => void) | undefined;
  /** Owner 23:35: „Dopożyczono: …” of an item (absent without the right to see the rentals). */
  rentedOf?: ((itemId: string) => string | undefined) | undefined;
  /** Owner 2026-10-23 20:53: Ctrl+klik (Windows: Alt+klik) on a line opens its item in the catalog. */
  onOpenCatalog?: ((itemId: string, name: string) => void) | undefined;
  /** Owner 2026-10-24 11:15: the ⚙ of the „Dodaj…” fields, as the quote's. */
  gear?: AddGear | undefined;
  /**
   * Owner 2026-10-24 („ma być jak w kosztorysie … nic nie ma ładować na dole”): the suppliers' gear, each line in its
   * category group among ours (by name, as ours), marked with the supplier's stripe and name.
   */
  suppliers?: readonly SupplierSection[] | undefined;
  /** The supplier of a quote's line may be changed here (the quote's way). */
  supplierEditable?: boolean;
  onSupplierChanged?: () => void;
  /** Ctrl+klik (Windows: Alt+klik) on a supplier's line: its position in the supplier's catalog. */
  onOpenSupplierCatalog?: ((t: CatalogTarget, name: string) => void) | undefined;
  /** Owner 2026-10-24 20:36: the × of a supplier's line — given only for the lines the person may remove. */
  onSupplierRemove?: ((placed: PlacedSupplierLine) => void) | undefined;
  maySupplierRemove?: ((placed: PlacedSupplierLine) => boolean) | undefined;
};

/** A row of a category group: our line or a supplier's. */
type Entry = { kind: 'own'; line: LineView } | { kind: 'supplier'; placed: PlacedSupplierLine; key: string };
const entryName = (e: Entry) => (e.kind === 'own' ? e.line.itemName : e.placed.line.name);

/** The line whose comments a notification opens (`?wiersz=<id>`, see the packlists module's `commentLink`). */
const linkedLine = () => (typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('wiersz'));

/**
 * The packlist as one table, like its PDF (Kategoria | Sprzęt | Ilość | …): a section per catalog category in the
 * catalog's order with air between them, each ending with its own quick picker; under the table one more picker
 * for anything and "Dodaj kategorię", which opens an empty section for a category of the catalog.
 */
export function PacklistSheet({ eventId, lines, categories, sourceNames, ruleOutcomes, editable, cols, selected, onToggleSelect, onRemove, onReload, me = null, onSubrent, rentedOf, onOpenCatalog, gear, suppliers = [], supplierEditable = false, onSupplierChanged = () => undefined, onOpenSupplierCatalog, onSupplierRemove, maySupplierRemove }: Props) {
  const comments = usePacklistComments(me, eventId);
  /** The line whose comments are open (one at a time). */
  const [openComments, setOpenComments] = useState<string | null>(linkedLine);
  const scrolledTo = useRef<string | null>(null);
  useEffect(() => {
    // a notification's link: bring its line into view once the list is there
    const id = openComments;
    if (!id || scrolledTo.current === id || !lines.some((l) => l.id === id)) return;
    scrolledTo.current = id;
    document.querySelector(`[data-line-id="${CSS.escape(id)}"]`)?.scrollIntoView?.({ block: 'center' });
  }, [openComments, lines]);
  /** Sections opened with "Dodaj kategorię" that have no lines yet (kept only on this screen). */
  const [opened, setOpened] = useState<string[]>([]);
  const [focusSection, setFocusSection] = useState<string | null>(null);
  /** The line just added: its quantity cell takes the focus (keyboard flow of the quick picker). */
  const [focusLineId, setFocusLineId] = useState<string | null>(null);
  /** The picker that added it, to go back to after Enter in the quantity. */
  const returnTo = useRef<HTMLInputElement | null>(null);
  const pickers = useRef(new Map<string, HTMLInputElement>());

  useEffect(() => {
    if (focusSection === null) return;
    pickers.current.get(focusSection)?.focus();
    setFocusSection(null);
  }, [focusSection]);

  const names = new Map(categories.map((c) => [c.id, c.label]));
  const rank = new Map(categories.map((c, i) => [c.id, i]));
  const known = new Set(names.keys());
  const groups = new Map<string, Entry[]>();
  for (const l of lines) {
    const key = l.categoryId && names.has(l.categoryId) ? l.categoryId : '';
    groups.set(key, [...(groups.get(key) ?? []), { kind: 'own', line: l }]);
  }
  for (const s of suppliers) {
    s.lines.forEach((line, i) => {
      const key = supplierGroupKey(line, known);
      const entry: Entry = { kind: 'supplier', placed: { line, supplierId: s.supplierId, supplierName: s.supplierName }, key: `${s.supplierId}-${line.quoteLineId ?? line.name}-${i}` };
      groups.set(key, [...(groups.get(key) ?? []), entry]);
    });
  }
  // by name, as our lines come (stable: ours keep their order, a supplier's keep the quote's) — never jumping to the end
  for (const g of groups.values()) g.sort((a, b) => entryName(a).localeCompare(entryName(b), 'pl'));
  for (const id of opened) if (names.has(id) && !groups.has(id)) groups.set(id, []);
  const ordered = [...groups.entries()].sort(([a], [b]) => (a === '' ? Infinity : (rank.get(a) ?? 0)) - (b === '' ? Infinity : (rank.get(b) ?? 0)));
  const free = categories.filter((c) => !groups.has(c.id));
  const duplicateCount = new Map(duplicateGroups(lines).flatMap((g) => g.map((l) => [l.id, g.length] as const)));
  const span = (cols.select ? 1 : 0) + 1 + (cols.quantity ? 1 : 0) + (cols.window ? 1 : 0) + 1 + (cols.notes ? 1 : 0) + (editable ? 1 : 0);

  const added = (lineId: string | null) => {
    const active = document.activeElement;
    returnTo.current = active instanceof HTMLInputElement ? active : null;
    if (lineId) setFocusLineId(lineId);
    else returnTo.current?.focus();
  };
  const pickerRef = (key: string) => (el: HTMLInputElement | null) => {
    if (el) pickers.current.set(key, el);
    else pickers.current.delete(key);
  };

  return (
    <div className="pl-sheet" data-feature-id="packlists.edit">
      <div className="table-wrap">
        <table className="table sheet-table pl-table" aria-label="Sprzętówka">
          <thead>
            <tr>
              <th scope="col" className="sheet-section-col">Kategoria</th>
              {cols.select && (
                <th scope="col" className="pl-check">
                  <span className="visually-hidden">Zaznacz do bloku</span>
                </th>
              )}
              <th scope="col">Sprzęt</th>
              {cols.quantity && <th scope="col" className="num">Ilość</th>}
              {cols.window && <th scope="col">Okno</th>}
              <th scope="col">Rezerwacja</th>
              {cols.notes && <th scope="col">Uwagi</th>}
              {editable && (
                <th scope="col">
                  <span className="visually-hidden">Działania</span>
                </th>
              )}
            </tr>
          </thead>
          {ordered.map(([key, group]) => {
            const label = key === '' ? NO_CATEGORY : names.get(key)!;
            return (
              <SheetSection key={key || 'none'} name={label}>
                {group.map((e, i) => {
                  const sectionCell = i === 0 ? <span className="sheet-section-name">{label}</span> : null;
                  if (e.kind === 'supplier')
                    return (
                      <SupplierLineRow
                        key={e.key}
                        placed={e.placed}
                        sectionCell={sectionCell}
                        cols={cols}
                        editable={editable}
                        supplierEditable={supplierEditable}
                        onChanged={onSupplierChanged}
                        onOpenCatalog={onOpenSupplierCatalog}
                        onRemove={editable && onSupplierRemove && maySupplierRemove?.(e.placed) ? () => onSupplierRemove(e.placed) : undefined}
                      />
                    );
                  const l = e.line;
                  return (
                  <Fragment key={`${l.id}-${l.version}`}>
                    <PacklistLineRow
                      eventId={eventId}
                      line={l}
                      sectionCell={sectionCell}
                      sourceNames={sourceNames}
                      cols={cols}
                      editable={editable}
                      {...(cols.select && l.quantity !== undefined ? { selected: selected.has(l.id), onToggleSelect: () => onToggleSelect(l.id) } : {})}
                      onRemove={onRemove}
                      onReload={onReload}
                      onSubrent={onSubrent}
                      rented={rentedOf?.(l.itemId)}
                      openCatalog={onOpenCatalog ? () => onOpenCatalog(l.itemId, l.itemName) : undefined}
                      duplicates={duplicateCount.get(l.id) ?? 1}
                      ruleOutcomes={ruleOutcomes}
                      focusQuantity={l.id === focusLineId}
                      onQuantityFocused={() => setFocusLineId(null)}
                      onQuantityDone={() => returnTo.current?.focus()}
                      {...(comments.data ? { comments: { count: comments.data.counts[l.id] ?? 0, open: openComments === l.id, onToggle: () => setOpenComments(openComments === l.id ? null : l.id) } } : {})}
                    />
                    {me && comments.data && openComments === l.id && (
                      <tr className="pl-comments-row">
                        <th scope="row" className="sheet-section-cell" />
                        <td colSpan={span}>
                          <LineComments eventId={eventId} lineId={l.id} name={l.itemName} comments={comments.data.comments.filter((c) => c.lineId === l.id)} me={me} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                  );
                })}
                {group.length === 0 && (
                  <tr className="sheet-empty">
                    <th scope="row" className="sheet-section-cell">
                      <span className="sheet-section-name">{label}</span>
                    </th>
                    <td colSpan={span} className="muted">
                      Brak pozycji.{' '}
                      <button className="btn small ghost" type="button" onClick={() => setOpened(opened.filter((id) => id !== key))}>
                        Usuń sekcję
                      </button>
                    </td>
                  </tr>
                )}
                {editable && key !== '' && (
                  <SheetAddRow span={span}>
                    <QuickAdd eventId={eventId} category={label} categoryId={key} inTable inputRef={pickerRef(key)} onAdded={added} gear={gear} />
                  </SheetAddRow>
                )}
              </SheetSection>
            );
          })}
          {lines.length === 0 && ordered.length === 0 && (
            <tbody>
              <tr className="sheet-empty">
                <th scope="row" className="sheet-section-cell" />
                <td colSpan={span} className="muted">
                  Sprzętówka jest pusta. Dodaj sprzęt albo zestaw poniżej, zacznij od kategorii albo wstaw blok lub szablon z „…” w prawym górnym rogu.
                </td>
              </tr>
            </tbody>
          )}
          {editable && (
            <tbody className="pl-foot">
              <tr className="sheet-add">
                <th scope="row" className="sheet-section-cell">
                  {free.length > 0 && (
                    <select
                      className="pl-add-category"
                      aria-label="Dodaj kategorię"
                      value=""
                      onChange={(e) => {
                        const id = e.target.value;
                        if (!id) return;
                        setOpened([...opened, id]);
                        setFocusSection(id);
                      }}
                    >
                      <option value="">+ Kategoria</option>
                      {free.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  )}
                </th>
                <td colSpan={span}>
                  <QuickAdd eventId={eventId} inTable inputRef={pickerRef('')} onAdded={added} gear={gear} />
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
}
